import ApiService from './api';

import { Tree } from '@/utils/tree';
import TaskScheduler from '@/utils/taskScheduler';
import ProductService from './product';

/** A content "box" record as returned by the intent content endpoints. Shape varies by template, so extra keys are allowed. */
export interface ContentBox {
  /** Unique box identifier within the intent's content tree */
  box: string;
  /** Template name of the box (e.g. 'AnswerInfo', 'YesNoQuestionInfo') */
  template: string;
  /** Channel ID this box variant belongs to; absent/null for the root channel */
  channel?: string | null;
  /** Paths/branches for path-based templates (YesNoQuestionInfo, MultipleChoiceInfo, ...) */
  answers?: any[];
  /** Follow-up pointer to the next box */
  followup?: any;
  /** Client-side flag set while the box body is still being fetched */
  loading?: boolean;
  /** Remaining template-specific fields not modeled here */
  [key: string]: any;
}

/**
 * Extra query options recorded for stats when saving content.
 * Values are serialized into the query string.
 */
export interface SaveContentOptions {
  /** Apply changes to live bot instead of staging (default false) */
  useLive?: boolean;
  /** For stats: Arbitrary identifier where the HTTP request came from, e.g. "playground:RAGTopic:override" or "/knowledge-base". */
  source?: string;
  /** For stats: Array of properties that was modified. */
  editedProperties?: string[];
  /** Additional arbitrary query params */
  [key: string]: number | string | boolean | null | undefined | Array<number | string | boolean | null>;
}

const ContentService = {
  /**
   * Fetches a single content box.
   * @param botId ID of the bot
   * @param template Template name of the box
   * @param intent Intent the content belongs to
   * @param box Box identifier; omit to fetch the root box of the template
   * @param channel Channel ID for channel-specific content; omit for root channel
   * @param options `useLive: true` reads from the live bot instead of staging
   * @returns The API envelope `{status, result}`; empty object on error
   */
  getContent: async function (
    botId: string,
    template: string,
    intent: string,
    box?: string | null,
    channel?: string | null,
    options: { useLive?: boolean } = {}
  ): Promise<{ status: string; result: any } | Record<string, never>> {
    Object.assign({
      useLive: false,
    }, options);

    try {
      let url = `/schaltzentrale/intent/content/${botId}/${intent}/${template}`;
      if (box) {
        url += `/box/${box}`;
      } else {
        url += `/box`;
      }

      if (channel) {
        url += `/channel/${channel}`;
      } else {
        url += `/channel`;
      }

      if (options.useLive) {
        url += '?useLive=true';
      }

      const response = await ApiService.get(url);
      return response.data;
    } catch (error) {
      console.error(error);
      return {};
    }
  },
  /**
   * Fetches all child boxes of an intent's content tree.
   * @param botId ID of the bot
   * @param template Template name of the root box
   * @param intent Intent the content belongs to
   * @param options `useLive` reads live content; `channel` scopes to a channel ID
   * @returns Array of child boxes; empty array on error
   */
  getContentChilds: async function (
    botId: string,
    template: string,
    intent: string,
    options: { useLive?: boolean; channel?: string | null } = {}
  ): Promise<any[]> {
    Object.assign({
      useLive: false,
      channel: null,
    }, options);

    const search = new URLSearchParams();
    if (options.useLive) {
      search.append('useLive', 'true');
    }
    if (options.channel) {
      search.append('channel', options.channel);
    }

    try {
      let url = `/schaltzentrale/intent/${template}/${intent}/${botId}/childboxes`;

      if (search.size) {
        url += `?${search.toString()}`;
      }

      const response = await ApiService.get(url);
      if (
        response.data &&
        response.data.status === 'ok' &&
        Array.isArray(response.data.childBoxes)
      ) {
        return response.data.childBoxes;
      }

      return [];
    } catch (error) {
      return [];
    }
  },
  /**
   * Persists a content payload for an intent.
   * @param botId ID of the bot
   * @param box Not used?
   * @param content The content payload to save
   * @param options Pass `true`/`false` for backwards-compatible useLive, or an options object for stats plumbing.
   */
  saveContent: async function (
    botId: string,
    box: string | null | undefined,
    content: Record<string, any>,
    options: SaveContentOptions | boolean = {}
  ): Promise<any> {
    try {
      let url = '/schaltzentrale/intent/' + botId;

      // Backwards compatibility: callers used to pass a boolean `useLive`.
      const normalizedOptions =
        typeof options === 'boolean' ? { useLive: options } : { ...options };

      const query = new URLSearchParams(normalizedOptions as unknown as Record<string, string>);
      const queryString = query.toString();
      if (queryString) {
        url += `?${queryString}`;
      }

      const response = await ApiService.post(
        url,
        content
      );
      return response.data;
    } catch (error) {
      return {};
    }
  },
  /**
   * Async generator that yields the content tree incrementally: first the root box,
   * then the root with loading children, then once per resolved child.
   * @param botId ID of the bot
   * @param template Template name of the root box
   * @param intent Intent the content belongs to
   * @param channel Channel ID for channel-specific content
   * @param options `useLive: true` reads from the live bot instead of staging
   */
  _getContents: async function* (
    botId: string,
    template: string,
    intent: string,
    channel: string | null | undefined,
    options: { useLive?: boolean } = {}
  ): AsyncGenerator<{ contents: Record<string, any>; tree: any }, null | void, unknown> {
    Object.assign({
      useLive: false,
    }, options);

    // get the root box for template, intent and channel
    let originContent: any = await ContentService.getContent(
      botId,
      template,
      intent,
      null,
      channel,
      options,
    );

    if (originContent?.result?.createChannel === true) {
      console.warn('No channel specific content found, trying to copy from root channel');
      await ProductService.copyChannel({
        sourceBotId: botId,
        targetBotId: botId,
        intentName: intent,
        targetIntent: intent,
        channelFrom: null,
        channelTo: channel,
      });

      originContent = await ContentService.getContent(
        botId,
        template,
        intent,
        null,
        channel,
        options,
      );
    }

    if (
      !originContent ||
      !originContent.result ||
      originContent.result.onlyDraft !== false ||
      originContent.result.createChannel !== false
    ) {
      // error, nor root box found
      return null;
    }
    let contents: Record<string, any> = {};
    contents[originContent.result.box] = originContent.result;

    let contentTree = new Tree();
    contentTree.setRoot(originContent.result);
    contentTree.buildTree(contents);

    yield { contents, tree: contentTree }; // return the root box first

    // now get all childs
    const children = await ContentService.getContentChilds(
      botId,
      template,
      intent,
      {
        ...options,
        channel: channel,
      }
    );

    contents = children.reduce((acc, child) => {
      child.loading = true;
      acc[child.box] = child;
      return acc;
    }, contents);

    // Perform some auto-repair in case of broken records.
    await this._fixPathBasedToPathBasedBoxes(contents, botId, intent, channel);

    contentTree = new Tree();
    contentTree.setRoot(originContent.result);
    contentTree.buildTree(contents);

    // return the root box with loading childs
    yield { contents, tree: contentTree };

    for (const child of children) {
      const content: any = await ContentService.getContent(
        botId,
        child.template,
        intent,
        child.box,
        channel,
        options,
      );

      if (!content.result || (content.result.channel && (channel !== content.result.channel))) {
        console.error('channel mismatch', content.channel, channel);
        continue;
      }

      contents[child.box] = content.result;

      // Perform some auto-repair in case of broken records.
      await this._fixPathBasedToPathBasedBoxes(contents, botId, intent, channel);

      contentTree = new Tree();
      contentTree.setRoot(originContent.result);
      contentTree.buildTree(contents);

      // return the next child
      yield { contents, tree: contentTree };
    }
  },
  /**
   * Fetches the full content tree (root + all children) for an intent.
   * @param botId ID of the bot
   * @param template Template name of the root box
   * @param intent Intent the content belongs to
   * @param channel Channel ID for channel-specific content
   * @param options `useLive` reads live content; `concurrency` > 0 throttles parallel child fetches
   * @returns All boxes keyed by box ID plus the built Tree; null on error
   */
  getContents: async function (
    botId: string,
    template: string,
    intent: string,
    channel: string | null | undefined,
    options: { useLive?: boolean; concurrency?: number } = {}
  ): Promise<{ contents: Record<string, any>; tree: any } | null> {
    Object.assign({
      useLive: false,
      concurrency: 0,
    }, options);

    const originContent: any = await ContentService.getContent(
      botId,
      template,
      intent,
      null,
      channel,
      options,
    );

    if (!originContent || !originContent.result) {
      // error
      return null;
    }
    if (!options.useLive &&
      (originContent.result.onlyDraft !== false ||
      originContent.result.createChannel !== false)
    ) {
      return null;
    }

    const children = await ContentService.getContentChilds(
      botId,
      template,
      intent,
      {
        ...options,
        channel,
      }
    );

    // Sets up the function that will retrieve each record
    const promiseHandler = async (child: any) => {
      const content: any = await ContentService.getContent(
        botId,
        child.template,
        intent,
        child.box,
        channel,
        options,
      );
      if (content && content.status === 'ok' && content.result) {
        return content.result;
      } else {
        throw false;
      }
    }

    let childPromises: Promise<any>[];

    // If we want controlled concurrency, we use a task scheduler
    if (options.concurrency! > 0) {
      const scheduler = new TaskScheduler(options.concurrency);
      childPromises = children.map(child => {
        return scheduler.addToQueue(() => promiseHandler(child));
      });
    }
    // ... else run them all at once
    else {
      childPromises = children.map(child => {
        return new Promise(async (resolve, reject) => {
          try {
            const r = await promiseHandler(child);
            return resolve(r);
          } catch(e) {
            return reject(e);
          }
        });
      });
    }

    // Set up the main content object that holds all boxes
    const childs = await Promise.all(childPromises);
    const contents = childs.reduce((acc, child) => {
      acc[child.box] = child;
      return acc;
    }, {} as Record<string, any>);
    contents[originContent.result.box] = originContent.result;

    // Perform some auto-repair in case of broken records.
    await this._fixPathBasedToPathBasedBoxes(contents, botId, intent, channel);

    // Set up and build the tree
    const contentTree = new Tree();
    contentTree.setRoot(originContent.result);
    contentTree.buildTree((box: string) => {
      return contents[box];
    });

    return { contents: contents, tree: contentTree };
  },
  /**
   * For boxes that has paths.
   * Ensures that all paths point to a non-path box and not happy path.
   * If it does not, will inject AnswerInfo in between them.
   *
   * For example, if a YesNoQuestionInfo points directly to MultipleChoiceInfo,
   * then MultipleChoiceInfo will disappear when you save.
   *
   * Usually handled automatically when adding a new box, but some old intents might be in a broken state already.
   * Mutates the input boxObject if necessary.
   *
   * @see https://github.com/knowhereto/moin-hub/pull/1354
   * @private
   * @param content A container with all the boxes
   * @param botId ID of the bot
   * @param intent Intent the content belongs to
   * @param channelId Channel ID for channel-specific content
   * @returns Mutates the content object directly and the objects within.
   */
  async _fixPathBasedToPathBasedBoxes(
    content: Record<string, any>,
    botId: string,
    intent: string,
    channelId?: string | null
  ): Promise<void> {
    let answerInfoBase: any = null; // Lazy fetch
    let n = 0;

    // Iterate over every box in content
    for (const boxObj of Object.values(content)) {
      // If box is loading, skip. We'll call auto-fix again later.
      if (boxObj.loading) continue;

      // Skip non-path based boxes
      if (!['YesNoQuestionInfo', 'MultipleChoiceInfo', 'MultipleChoiceSlider'].includes(boxObj.template)) continue;

      // Iterate over each path
      for (const answer of boxObj.answers) {
        // Skip if the answer points to a non-path box or not happy path
        if (!['YesNoQuestionInfo', 'MultipleChoiceInfo', 'MultipleChoiceSlider', 'none'].includes(answer.template)) continue;

        // Prepare the new AnswerInfo to be injected
        if (!answerInfoBase) {
          const { result } = (await this.getContent(botId, 'AnswerInfo', intent, undefined, channelId)) as { result: any };
          answerInfoBase = result;
        }
        const answerInfo = JSON.parse(JSON.stringify(answerInfoBase));
        answerInfo.box = `${answerInfoBase.box}${n++}`;

        // Store the original path as followup for AnswerInfo
        answerInfo.followup = JSON.parse(JSON.stringify(answer));

        // Mutate the path point to the new AnswerInfo instead
        answer.template = answerInfo.template;
        answer.box = answerInfo.box;

        // Inject the AnswerInfo box into the master content map
        content[answerInfo.box] = answerInfo;
      }
    }
  },

  /**
   * Fetches the preview payload for a form email
   * @param botId Needed for auth only
   * @param actionParams The email action's parameters (recipients, subject, ...)
   * @param context Context variables collected by the form, used to render the preview
   */
  getFormEmailPreview: async function(botId: string, actionParams: any, context: any): Promise<{ status: string; result: any[] }> {
    try {
      const { data } = await ApiService.post('/schaltzentrale/intent/content/email-notification/preview', {
        actionParams,
        context,

        // Included in body for auth
        uniqueBotId: botId,
      });
      return data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  /**
   * Sends a test email simulating the form mail auto template
   * @param botId Needed for auth only
   * @param actionParams The email action's parameters (recipients, subject, ...)
   * @param context Context variables collected by the form, used to render the test mail
   */
  sendTestEmailNotification: async function(botId: string, actionParams: any, context: any): Promise<{ status: string; message: string }> {
    try {
      const { data } = await ApiService.post('/schaltzentrale/intent/content/email-notification/send-test', {
        actionParams,
        context,
        uniqueBotId: botId,
      });
      return data;
    } catch (e: any) {
      console.error(e);
      if (e.response && e.response.data) {
        return e.response.data;
      }
      return { status: 'error', message: e.message || 'Network or server error.' };
    }
  }
};


export default ContentService;

export { ContentService };
